/**
 * Verification Persistence Utility
 * Helps preserve user data when verification is rejected
 */

interface RejectedFieldInfo {
  verificationId: string;
  userId: string;
  rejectedAt: string;
  rejectedFields: string[];
  rejectionReason: string;
  preservedData: any; // Original data that can be restored
}

const STORAGE_KEY_PREFIX = 'verification_rejected_';

export const verificationPersistence = {
  /**
   * Save rejected field information to localStorage
   */
  saveRejectedFieldInfo(info: RejectedFieldInfo): void {
    try {
      const key = `${STORAGE_KEY_PREFIX}${info.userId}`;
      localStorage.setItem(key, JSON.stringify(info));
    } catch (error) {
      console.error('Failed to save rejected field info:', error);
    }
  },

  /**
   * Get rejected field information for a user
   */
  getRejectedFieldInfo(userId: string): RejectedFieldInfo | null {
    try {
      const key = `${STORAGE_KEY_PREFIX}${userId}`;
      const data = localStorage.getItem(key);
      if (data) {
        return JSON.parse(data);
      }
    } catch (error) {
      console.error('Failed to get rejected field info:', error);
    }
    return null;
  },

  /**
   * Clear rejected field information (e.g., after successful resubmission)
   */
  clearRejectedFieldInfo(userId: string): void {
    try {
      const key = `${STORAGE_KEY_PREFIX}${userId}`;
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear rejected field info:', error);
    }
  },

  /**
   * Check if specific fields were rejected
   */
  wasFieldRejected(userId: string, fieldName: string): boolean {
    const info = this.getRejectedFieldInfo(userId);
    return info ? info.rejectedFields.includes(fieldName) : false;
  },

  /**
   * Get preserved data for non-rejected fields
   */
  getPreservedData(userId: string): any {
    const info = this.getRejectedFieldInfo(userId);
    return info ? info.preservedData : null;
  },

  /**
   * Check if user has recent rejection (within 30 days)
   */
  hasRecentRejection(userId: string, daysThreshold: number = 30): boolean {
    const info = this.getRejectedFieldInfo(userId);
    if (!info) return false;
    
    const rejectedDate = new Date(info.rejectedAt);
    const daysSinceRejection = (Date.now() - rejectedDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceRejection <= daysThreshold;
  }
};